package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.material3.TextButton
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.api.ApiException
import org.gymly.data.AdminRepo
import org.gymly.data.PortalRepo
import org.gymly.model.LoginSuccess
import org.gymly.model.PublicCompanyBranding
import org.gymly.model.PublicMarketingStats
import org.gymly.theme.GymlyTouch
import org.gymly.util.userMessage
import org.gymly.ui.components.PasswordRecoveryDialog
import org.gymly.ui.components.PasswordVisibilityToggle
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.ui.components.rememberPasswordVisible

private val LoginDarkBlue: Color get() = org.gymly.theme.BrandTheme.loginPanel
private val LoginDarkBlueDeep: Color get() = org.gymly.theme.BrandTheme.loginPanelDeep
private val LoginHeroGrayBlue = Color(0xFFD9E2EC)
private val LoginFieldBorderOnDark = Color(0xFF8FA0C8)
private const val LoginHeroImage =
    "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=900&q=80"

/**
 * @param unified si true (web): intenta login staff y luego cliente.
 * @param onLoggedIn éxito admin o portal según credenciales.
 */
@Composable
fun AdminLoginScreen(
    unified: Boolean = false,
    onLoggedIn: (LoginSuccess) -> Unit,
) {
    var email by remember { mutableStateOf(if (unified) "" else "admin@gymly.com") }
    var password by remember { mutableStateOf(if (unified) "" else "admin123") }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(false) }
    var branding by remember { mutableStateOf<PublicCompanyBranding?>(null) }
    var stats by remember { mutableStateOf<PublicMarketingStats?>(null) }
    var statsLoading by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()
    // Solo se ofrece "¿Olvidaste tu contraseña?" si el gimnasio ya configuró el correo SMTP.
    var recoveryAvailable by remember { mutableStateOf(false) }
    var showRecovery by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        recoveryAvailable = runCatching { AdminRepo.api.passwordRecoveryAvailable() }.getOrDefault(false)
        branding = runCatching { AdminRepo.api.publicCompany() }.getOrNull()
        org.gymly.theme.BrandTheme.apply(branding?.brandColor)
        statsLoading = true
        stats = runCatching { AdminRepo.api.publicStats() }.getOrNull()
        statsLoading = false
    }
    fun doLogin() {
        scope.launch {
            loading = true
            error = null
            try {
                if (unified) {
                    onLoggedIn(loginUnified(email, password))
                } else {
                    val user = AdminRepo.api.login(email, password)
                    onLoggedIn(LoginSuccess.Admin(user))
                }
            } catch (e: Throwable) {
                error = e.userMessage("No se pudo iniciar sesión.")
            } finally {
                loading = false
            }
        }
    }

    if (showRecovery) {
        // En el login unificado (web) la cuenta puede ser de personal o de socio: se prueban ambas.
        val recoveryScopes = if (unified) listOf("STAFF", "CLIENT") else listOf("STAFF")
        PasswordRecoveryDialog(
            scopes = recoveryScopes,
            initialEmail = email,
            request = { s, e ->
                if (s == "CLIENT") PortalRepo.api.requestPasswordRecovery(s, e) else AdminRepo.api.requestPasswordRecovery(s, e)
            },
            confirm = { s, e, code, pw ->
                if (s == "CLIENT") {
                    PortalRepo.api.confirmPasswordRecovery(s, e, code, pw)
                } else {
                    AdminRepo.api.confirmPasswordRecovery(s, e, code, pw)
                }
            },
            onDismiss = { showRecovery = false },
        )
    }

    Column(Modifier.fillMaxSize().background(Color.White)) {
        LoginTopBar(branding)
        BoxWithConstraints(Modifier.weight(1f).fillMaxWidth()) {
            val wide = maxWidth >= 900.dp
            if (wide) {
                Row(Modifier.fillMaxSize()) {
                    LoginHeroPanel(
                        branding = branding,
                        modifier = Modifier.weight(1.05f).fillMaxHeight(),
                    )
                    LoginFormColumn(
                        unified = unified,
                        companyName = branding?.name,
                        email = email,
                        onEmail = { email = it },
                        password = password,
                        onPassword = { password = it },
                        error = error,
                        loading = loading,
                        onLogin = ::doLogin,
                        recoveryAvailable = recoveryAvailable,
                        onForgotPassword = { showRecovery = true },
                        modifier = Modifier.weight(1f).fillMaxHeight(),
                    )
                }
            } else {
                VerticalScrollColumn(
                    Modifier
                        .fillMaxSize(),
        ) {
                    LoginHeroPanel(
                        branding = branding,
                        modifier = Modifier.fillMaxWidth().height(300.dp),
                    )
                    LoginFormColumn(
                        unified = unified,
                        companyName = branding?.name,
                        email = email,
                        onEmail = { email = it },
                        password = password,
                        onPassword = { password = it },
                        error = error,
                        loading = loading,
                        onLogin = ::doLogin,
                        recoveryAvailable = recoveryAvailable,
                        onForgotPassword = { showRecovery = true },
                        scrollable = false,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            }
        }
        LoginFooter(branding = branding, stats = stats, statsLoading = statsLoading)
    }
}

private suspend fun loginUnified(email: String, password: String): LoginSuccess {
    val staffResult = runCatching { AdminRepo.api.login(email, password) }
    staffResult.getOrNull()?.let { return LoginSuccess.Admin(it) }

    val clientResult = runCatching { PortalRepo.api.login(email, password) }
    clientResult.getOrNull()?.let { return LoginSuccess.Portal(it) }

    val staffError = staffResult.exceptionOrNull()
    val clientError = clientResult.exceptionOrNull()
    fun isUnauthorized(e: Throwable?): Boolean {
        if (e == null) return false
        if (e is ApiException && e.status == 401) return true
        val msg = e.message.orEmpty()
        return msg.contains("Invalid credentials", ignoreCase = true)
            || msg.contains("incorrectos", ignoreCase = true)
    }
    fun isForbidden(e: Throwable?): Boolean =
        e is ApiException && e.status == 403
    if (isUnauthorized(staffError) || isUnauthorized(clientError)) {
        error("Correo o contraseña incorrectos.")
    }
    if (isForbidden(staffError) || isForbidden(clientError)) {
        error("Acceso bloqueado por el servidor (CORS/seguridad). Revisa la configuración del API.")
    }
    throw (clientError ?: staffError ?: IllegalStateException("No se pudo iniciar sesión."))
}

@Composable
private fun LoginTopBar(branding: PublicCompanyBranding?) {
    val name = branding?.name?.takeIf { it.isNotBlank() } ?: "Gymly"
    val logoUrl = branding?.takeIf { it.hasLogo }?.logoUrl?.takeIf { it.isNotBlank() }
    Row(
        Modifier
            .fillMaxWidth()
            .background(Color.White)
            .padding(horizontal = 28.dp, vertical = 16.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        org.gymly.ui.components.BrandLogo(
            logoUrl = logoUrl,
            name = name,
            height = 36.dp,
        ) {
            Box(
                Modifier
                    .size(36.dp)
                    .clip(CircleShape)
                    .background(LoginDarkBlue),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    name.take(1).uppercase(),
                    color = Color.White,
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp,
                )
            }
        }
        Text(
            name,
            color = LoginDarkBlueDeep,
            fontWeight = FontWeight.Bold,
            fontSize = 22.sp,
            modifier = Modifier.padding(start = 10.dp),
        )
    }
}

@Composable
private fun LoginHeroPanel(
    branding: PublicCompanyBranding?,
    modifier: Modifier = Modifier,
) {
    val hero = branding?.takeIf { it.hasBanner }?.bannerUrl?.takeIf { it.isNotBlank() } ?: LoginHeroImage
    Box(
        modifier.background(LoginHeroGrayBlue),
    ) {
        AsyncImage(
            model = hero,
            contentDescription = null,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop,
            alignment = Alignment.Center,
        )
    }
}

@Composable
private fun LoginFormColumn(
    unified: Boolean,
    companyName: String?,
    email: String,
    onEmail: (String) -> Unit,
    password: String,
    onPassword: (String) -> Unit,
    error: String?,
    loading: Boolean,
    onLogin: () -> Unit,
    recoveryAvailable: Boolean,
    onForgotPassword: () -> Unit,
    scrollable: Boolean = true,
    modifier: Modifier = Modifier,
) {
    val brand = companyName?.takeIf { it.isNotBlank() } ?: "Gymly"
    val fields: @Composable ColumnScope.() -> Unit = {
        Text(
            if (unified) "Iniciar sesión en $brand" else "Iniciar sesión en $brand Admin",
            fontWeight = FontWeight.Bold,
            fontSize = 26.sp,
            lineHeight = 32.sp,
            color = Color.White,
            maxLines = 1,
            softWrap = false,
            overflow = TextOverflow.Ellipsis,
        )
        Spacer(Modifier.height(8.dp))
        Text(
            "Accede a tu cuenta",
            color = Color.White.copy(alpha = 0.75f),
            fontSize = 15.sp,
        )
        Spacer(Modifier.height(32.dp))
        error?.let {
            Text(
                it,
                color = Color(0xFFFFB4AB),
                fontSize = 13.sp,
                modifier = Modifier.padding(bottom = 12.dp),
            )
        }
        LoginOutlinedField(
            value = email,
            onValueChange = onEmail,
            placeholder = "Correo electrónico",
        )
        Spacer(Modifier.height(16.dp))
        LoginOutlinedField(
            value = password,
            onValueChange = onPassword,
            placeholder = "Contraseña",
            isPassword = true,
        )
        Spacer(Modifier.height(24.dp))
        Button(
            onClick = onLogin,
            modifier = Modifier.fillMaxWidth().height(GymlyTouch.minHeight),
            enabled = !loading,
            shape = RoundedCornerShape(8.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = Color.White,
                contentColor = LoginDarkBlueDeep,
            ),
        ) {
            if (loading) {
                CircularProgressIndicator(
                    color = LoginDarkBlueDeep,
                    modifier = Modifier.size(22.dp),
                    strokeWidth = 2.dp,
                )
            } else {
                Text("Iniciar sesión", fontWeight = FontWeight.Bold)
            }
        }
        if (recoveryAvailable) {
            TextButton(onClick = onForgotPassword) {
                Text("¿Olvidaste tu contraseña?", color = Color.White.copy(alpha = 0.85f))
            }
        }
    }
    Box(
        modifier
            .background(LoginDarkBlueDeep)
            .padding(horizontal = 48.dp, vertical = 32.dp),
        contentAlignment = Alignment.Center,
    ) {
        val formModifier = Modifier
            .widthIn(max = 400.dp)
            .fillMaxWidth()
        if (scrollable) {
            VerticalScrollColumn(formModifier, verticalArrangement = Arrangement.Center, content = fields)
        } else {
            Column(formModifier, content = fields)
        }
    }
}

@Composable
private fun LoginOutlinedField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    isPassword: Boolean = false,
) {
    val (visible, toggleVisible) = rememberPasswordVisible()
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text(placeholder, color = Color.White.copy(alpha = 0.55f)) },
        visualTransformation = if (isPassword && !visible) PasswordVisualTransformation() else VisualTransformation.None,
        trailingIcon = if (isPassword) {
            { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible, tint = Color.White) }
        } else {
            null
        },
        shape = RoundedCornerShape(8.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedTextColor = Color.White,
            unfocusedTextColor = Color.White,
            cursorColor = Color.White,
            focusedBorderColor = Color.White,
            unfocusedBorderColor = LoginFieldBorderOnDark,
            focusedContainerColor = LoginDarkBlue.copy(alpha = 0.35f),
            unfocusedContainerColor = LoginDarkBlue.copy(alpha = 0.25f),
        ),
    )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun LoginFooter(
    branding: PublicCompanyBranding?,
    stats: PublicMarketingStats?,
    statsLoading: Boolean,
) {
    val showClients = branding?.loginShowClients ?: true
    val showTrainers = branding?.loginShowTrainers ?: true
    val showOnTime = branding?.loginShowOnTime ?: true
    val clients = when {
        statsLoading -> "…"
        stats != null -> stats.activeClients.toString()
        else -> "0"
    }
    val trainers = when {
        statsLoading -> "…"
        stats != null -> stats.trainers.toString()
        else -> "0"
    }
    val onTime = when {
        statsLoading -> "…"
        stats != null -> "${stats.onTimePaymentPercent}%"
        else -> "0%"
    }

    BoxWithConstraints(
        Modifier
            .fillMaxWidth()
            .background(LoginDarkBlueDeep)
            .padding(horizontal = 24.dp, vertical = 18.dp),
    ) {
        val compact = maxWidth < AdminLayoutBreakpoints.COMPACT_MAX.dp
        if (compact) {
            // Celular: logo (solo el círculo) a la izquierda y estadísticas a la derecha.
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top,
            ) {
                GymlyProductMark(showName = false)
                FlowRow(
                    Modifier.weight(1f).padding(start = 12.dp),
                    horizontalArrangement = Arrangement.End,
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    if (showClients) {
                        FooterStat(clients, "clientes activos", small = true)
                    }
                    if (showTrainers) {
                        FooterStat(trainers, "entrenadores", small = true)
                    }
                    if (showOnTime) {
                        FooterStat(onTime, "pagos a tiempo", small = true)
                    }
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                GymlyProductMark()
                Row(horizontalArrangement = Arrangement.spacedBy(40.dp)) {
                    if (showClients) {
                        FooterStat(clients, "clientes activos")
                    }
                    if (showTrainers) {
                        FooterStat(trainers, "entrenadores")
                    }
                    if (showOnTime) {
                        FooterStat(onTime, "pagos a tiempo")
                    }
                }
            }
        }
    }
}

@Composable
private fun GymlyProductMark(showName: Boolean = true) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Box(
            Modifier
                .size(28.dp)
                .clip(CircleShape)
                .background(Color(0xFF3B4CB8)),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                "G",
                color = Color.White,
                fontWeight = FontWeight.Bold,
                fontSize = 14.sp,
            )
        }
        if (showName) {
            Text(
                "Gymly",
                color = Color.White,
                fontWeight = FontWeight.Bold,
                fontSize = 16.sp,
                modifier = Modifier.padding(start = 8.dp),
            )
        }
    }
}

@Composable
private fun FooterStat(value: String, label: String, small: Boolean = false) {
    Column {
        Text(
            value,
            color = Color.White,
            fontWeight = FontWeight.Bold,
            fontSize = if (small) 16.sp else 22.sp,
            maxLines = 1,
        )
        Spacer(Modifier.height(2.dp))
        Text(
            label,
            color = Color.White.copy(alpha = 0.85f),
            fontSize = if (small) 11.sp else 13.sp,
            maxLines = 1,
        )
    }
}
